import { useCallback, useMemo, useReducer } from 'react';

import { getRefSummaries } from '../utils/firestore';
import { error as errorLabel } from '../label';
import { ip } from '../utils/functions/urls';
import getRequestMeta from '../utils/functions/generateMeta';

const getToken = async (u) => u.getIdToken();

const initialState = {
  summaries: null,
  endpoint: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setSupportInfo':
      return {
        ...state,
        summaries: action.summaries,
        endpoint: action.endpoint,
      };
    default:
      return state;
  }
};

function useJiraHelpDesk(currentUser) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const newTicket = useCallback(async (message, summary) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          summary,
          message,
        }),
      };

      const resFetch = await fetch(`${ip}/manage/ticket/send`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabel.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabel.useJiraHelpDesk.newTicket,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (er) {
      console.log(er);
      return { error: true, msg: errorLabel.useJiraHelpDesk.newTicket, raw: `Erro do sistema: ${er.toString()}` };
    }
  }, [currentUser]);

  const getSupportInfo = useCallback(async () => {
    try {
      const ref = getRefSummaries();
      const tmp = await ref.get();
      const data = tmp.data();
      return {
        error: false,
        msg: '',
        summaries: data?.summaries || [],
        endpoint: data?.endpoint || '',
      };
    } catch (er) {
      console.log(er);
      return { error: true, msg: errorLabel.useJiraHelpDesk.getSupportInfo, raw: `Erro do sistema: ${er.toString()}` };
    }
  }, [currentUser]);

  const middleware = useCallback(async (action) => {
    switch (action.type) {
      case 'newTicket': {
        return newTicket(action.message, action.summary);
      }
      case 'getSupportInfo': {
        const res = await getSupportInfo();
        if (!res.error) {
          dispatch({
            type: 'setSupportInfo',
            summaries: res.summaries,
            endpoint: res.endpoint,
          });
        }
        return res;
      }
      default:
        return { error: false, msg: '', raw: '' };
    }
  }, [currentUser.uid, newTicket, getSupportInfo]);

  const supportAPI = useMemo(() => ({
    newTicket: async (message, summary) => middleware({
      type: 'newTicket', message, summary,
    }),
    getSupportInfo: async () => middleware({ type: 'getSupportInfo' }),
  }), [middleware]);

  return [state, supportAPI];
}

export default useJiraHelpDesk;
